import React from 'react';
import { motion } from 'framer-motion';
import { ShieldIcon, AlertTriangleIcon } from 'lucide-react';
interface RiskPanelProps {
  showWarning: boolean;
  concentrationPct: number;
  phase: number;
  isRecomputing?: boolean;
}
export function RiskPanel({
  showWarning,
  concentrationPct,
  phase,
  isRecomputing
}: RiskPanelProps) {
  const limits = [{
    label: 'Obligor concentration',
    value: concentrationPct,
    limit: 20,
    status: showWarning ? 'warn' : 'pass',
    updates: true
  }, {
    label: 'Sector concentration',
    value: 31.0,
    limit: 35,
    status: 'pass'
  }, {
    label: 'Covenant breaches',
    value: 0,
    limit: 0,
    status: 'pass'
  }];
  return <div className={`bg-slate-900 border rounded p-3.5 h-full transition-colors ${isRecomputing ? 'border-emerald-500/50' : 'border-slate-800'}`} data-id="element-180">
      <div className="flex items-center justify-between mb-3 pt-2" data-id="element-181">
        <h3 className="text-base font-semibold text-slate-300 tracking-tight" data-id="element-182">
          Risk Management
        </h3>
        <ShieldIcon size={16} className="text-slate-600" data-id="element-183" />
      </div>

      <div className="space-y-3" data-id="element-184">
        {limits.map((limit, index) => <motion.div key={limit.label} initial={{
        opacity: 0,
        y: 5
      }} animate={{
        opacity: phase >= 4 ? 1 : 0,
        y: phase >= 4 ? 0 : 5
      }} transition={{
        delay: index * 0.05,
        duration: 0.2
      }} className="border border-slate-800 rounded p-2.5 bg-slate-900/50" data-id="element-185">
            <div className="flex items-start justify-between mb-1.5" data-id="element-186">
              <div className="text-[11px] text-slate-400" data-id="element-187">{limit.label}</div>
              <motion.div animate={{
            scale: showWarning && limit.status === 'warn' ? [1, 1.15, 1] : 1
          }} transition={{
            duration: 0.3,
            repeat: showWarning && limit.status === 'warn' ? Infinity : 0,
            repeatDelay: 2
          }} data-id="element-188">
                {limit.status === 'warn' ? <AlertTriangleIcon size={12} className="text-amber-500" data-id="element-189" /> : <div className={`h-1.5 w-1.5 rounded-full ${isRecomputing && limit.updates ? 'bg-emerald-400 animate-pulse' : 'bg-emerald-500'}`} data-id="element-190" />}
              </motion.div>
            </div>

            <div className="flex items-baseline gap-2" data-id="element-191">
              <motion.span key={limit.value} initial={{
            scale: 1
          }} animate={{
            scale: phase >= 8 && limit.updates || isRecomputing ? [1, 1.08, 1] : 1
          }} transition={{
            duration: 0.4
          }} className={`text-[22px] font-mono font-semibold leading-none ${isRecomputing && limit.updates ? 'text-emerald-400' : limit.status === 'warn' ? 'text-amber-400' : 'text-slate-300'}`} data-id="element-192">
                {typeof limit.value === 'number' && limit.value > 0 && limit.value < 100 ? `${limit.value.toFixed(1)}%` : limit.value}
              </motion.span>
              {limit.limit > 0 && <span className="text-[11px] text-slate-600" data-id="element-193">
                  / {limit.limit}% limit
                </span>}
            </div>

            {showWarning && limit.status === 'warn' && phase >= 4 && phase < 8 && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} transition={{
          duration: 0.2
        }} className="mt-1.5 text-[9px] text-amber-400" data-id="element-194">
                  Approaching {limit.limit}% obligor limit.
                </motion.div>}

            {phase >= 8 && limit.updates && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} transition={{
          duration: 0.2
        }} className="mt-1.5 text-[9px] text-emerald-400" data-id="element-195">
                Exposure shifted. Within limits.
              </motion.div>}
          </motion.div>)}
      </div>

      {phase >= 4 && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.25,
      duration: 0.2
    }} className="mt-3 pt-3 border-t border-slate-800" data-id="element-196">
          <div className="text-[10px] text-slate-400" data-id="element-197">
            Auto-detection active. Alerts fire before threshold breach.
          </div>
        </motion.div>}
    </div>;
}